import { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { zonaApi } from '../api/zonaApi';

export const useZonas = () => {
  const [zonas, setZonas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Obtener todas las zonas
  const fetchZonas = useCallback(async (proyectoId = null, soloActivas = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.getZonas(proyectoId, soloActivas);
      setZonas(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener zonas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener zonas con estadísticas
  const fetchZonasWithStats = useCallback(async (proyectoId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.getZonasWithStats(proyectoId);
      setZonas(response.data);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener zonas con estadísticas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener zona por ID
  const fetchZonaById = useCallback(async (zonaId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.getZonaById(zonaId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Crear zona
  const createZona = useCallback(async (zonaData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.createZona(zonaData);
      setZonas(prev => [...prev, response.data]);
      toast.success('Zona creada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al crear zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Actualizar zona
  const updateZona = useCallback(async (zonaId, zonaData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.updateZona(zonaId, zonaData);
      setZonas(prev => 
        prev.map(zona => 
          zona.id === zonaId ? response.data : zona
        )
      );
      toast.success('Zona actualizada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al actualizar zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Eliminar zona
  const deleteZona = useCallback(async (zonaId) => {
    setLoading(true);
    setError(null);
    
    try {
      await zonaApi.deleteZona(zonaId);
      setZonas(prev => prev.filter(zona => zona.id !== zonaId));
      toast.success('Zona eliminada exitosamente');
      return true;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al eliminar zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Buscar zonas
  const searchZonas = useCallback(async (searchTerm, proyectoId = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.searchZonas(searchTerm, proyectoId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al buscar zonas';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtener estadísticas de una zona
  const fetchZonaStatistics = useCallback(async (zonaId) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await zonaApi.getZonaStatistics(zonaId);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al obtener estadísticas de la zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Activar / inactivar zona
  const cambiarEstadoZona = useCallback(async (zonaId, activo) => {
    setLoading(true);
    setError(null);

    try {
      const response = await zonaApi.cambiarEstadoZona(zonaId, activo);
      setZonas(prev =>
        prev.map(zona =>
          zona.id === zonaId ? response.data : zona
        )
      );
      toast.success(activo ? 'Zona activada exitosamente' : 'Zona inactivada exitosamente');
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error al cambiar el estado de la zona';
      setError(errorMessage);
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  // Limpiar error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Estado
    zonas,
    loading,
    error,
    
    // Acciones
    fetchZonas,
    fetchZonasWithStats,
    fetchZonaById,
    createZona,
    updateZona,
    deleteZona,
    cambiarEstadoZona,
    searchZonas,
    fetchZonaStatistics,
    clearError,
  };
};
